<script setup>
import {useWizard} from "@/components/wizard";
import {computed, ref, useTemplateRef} from "vue";
import * as yup from "yup";
import {useDjangoForm} from "@/utils/forms";
import {mdiCheckCircle, mdiChevronLeft} from "@mdi/js";
import Modal from "@/components/modal/Modal.vue";
import WizardStep from "@/components/wizard/WizardStep.vue";
import Wizard from "@/components/wizard/Wizard.vue";
import {useMutation} from "@vue/apollo-composable";
import FormErrorDialog from "@/components/hpo/FormErrorDialog.vue";
import {useWizardLeaveConfirmation} from "@/components/wizard/composition";
import {pgettext} from "@/utils/translate";
import HospitalRecordsSummaryScreen from "./HospitalRecordsSummaryScreen.vue";
import HospitalRecordsScreen from "./HospitalRecordsScreen.vue";
import {stringValidator} from "../../../utils/yupValidators";
import HpoHospitalRecordsMutation from "@/graphql/mutations/hpo/hospitalRecords.graphql"

const props = defineProps({
  hpoId: {
    type: String,
    required: true
  },
  patientList: {
    type: Array,
    default: () => [],
  },
  patientData: {
    type: Object,
  },
});
const emit = defineEmits(['close']);

const errorDialog = useTemplateRef('errorDialog');

function close() {
  emit('close');
}

const isEditing = ref(!!props.patientData);

function getNextSummaryStep(patient) {
  if (patient.admittedToHospitalTimestamp || patient.temporaryIdentifier || patient.destinationWard) {
    return 'summary';
  }
  return 'hospitalRecords';
}

const wizard = useWizard({
  startStepName: getNextSummaryStep(props.patientData, true)
});


const hospitalRecordsStep = wizard.defineStep('hospitalRecords');
const summaryStep = wizard.defineStep('summary');
const confirmationStep = wizard.defineStep('confirmation', {
  enableBack: false,
});

wizard.onEnd(() => {
  close();
});

function editStep(stepName) {
  wizard.pushFlow(Array.isArray(stepName) ? stepName : [stepName]);
}

const schema = yup.object({
  temporaryIdentifier: stringValidator,
  admittedToHospitalTimestamp: yup.date().nullable(),
  destinationWard: stringValidator,
});

function formatDateTime(date) {
  try {
    return date.format('YYYY-MM-DDTHH:mm');
  } catch (e) {
    return null;
  }
}

const {
  defineField,
  handleMutationSubmit,
  values,
  isSubmitting,
  meta: formMeta,
  validateField
} = useDjangoForm({
  validationSchema: schema,
  initialValues: {
    temporaryIdentifier: props.patientData.temporaryIdentifier,
    admittedToHospitalTimestamp: props.patientData.admittedToHospitalTimestamp ? formatDateTime(props.patientData.admittedToHospitalTimestamp) : null,
    destinationWard: props.patientData.destinationWard
  },
});

const patientId = ref(props.patientData?.id || null);
const hospitalId = props.patientData.hospital.id;
const [temporaryIdentifier, temporaryIdentifierProps] = defineField('temporaryIdentifier');
const [admittedToHospitalTimestamp, admittedToHospitalTimestampProps] = defineField('admittedToHospitalTimestamp');
const [destinationWard, destinationWardProps] = defineField('destinationWard');


const hospitalRecordsProps = computed(() => {
  return {
    temporaryIdentifier: temporaryIdentifierProps.value,
    admittedToHospitalTimestamp: admittedToHospitalTimestampProps.value,
    destinationWard: destinationWardProps.value,
  };
});


const {mutate, loading} = useMutation(HpoHospitalRecordsMutation)

const onSubmit = handleMutationSubmit(
  async (data) => {
    if (data.admittedToHospitalTimestamp) {
      data.admittedToHospitalTimestamp = new Date(data.admittedToHospitalTimestamp)
    } else {
      data.admittedToHospitalTimestamp = null
    }

    const result = await mutate({
      patientId: patientId.value,
      hospitalId: hospitalId,
      ...data,
    });
    console.info('Updated hospital records for person with ID: ', patientId.value);
    return result;
  },
  {
    onSuccess(result) {
      wizard.goNext();
    },
    onMutationError(error) {
      errorDialog.value.showMutationError(error);
    },
    onInvalidFormResponse(error) {
      errorDialog.value.showValidationError(error);
    },
  }
);

const fieldDefinitions = {
  temporaryIdentifier: {label: pgettext('label', 'Temporary Identifier'), step: 'hospitalRecords'},
  admittedToHospitalTimestamp: {label: pgettext('label', 'Admitted to Hospital Timestamp'), step: 'hospitalRecords'},
  destinationWard: {label: pgettext('label', 'Destination Ward'), step: 'hospitalRecords'},
};

useWizardLeaveConfirmation(
  wizard,
  formMeta,
  pgettext('confirm', 'You have an unsaved identification. Are you sure you want to leave this page?'),
  [
    confirmationStep,
  ],
)
</script>

<template>
  <Modal fullscreen @close="close">
    <Wizard :wizard="wizard" :max-width="1300">
      <template #header>
        <div class="wizard__progress-header">
          <v-btn @click="wizard.goBack()"
                 :prepend-icon="mdiChevronLeft"
                 :disabled="!wizard.canGoBack()">
            {{ pgettext('action', 'Back') }}
          </v-btn>
          <div class="text-center">
          </div>
        </div>
      </template>


      <WizardStep :step="hospitalRecordsStep">
        <HospitalRecordsScreen v-model:temporaryIdentifier="temporaryIdentifier"
                               v-model:admittedToHospitalTimestamp="admittedToHospitalTimestamp"
                               v-model:destinationWard="destinationWard"
                               :formProps="hospitalRecordsProps"
                               :validateField="validateField"
                               @submit="wizard.goNext"/>
      </WizardStep>


      <WizardStep :step="summaryStep">
        <HospitalRecordsSummaryScreen :isEditing="isEditing"
                                      :formData="values"
                                      :tic="patientData.tic"
                                      :patientId="patientId"
                                      :isSubmitting="isSubmitting"
                                      @pushStep="editStep"
                                      @submit="onSubmit"/>
      </WizardStep>

      <WizardStep :step="confirmationStep">
        <v-empty-state :icon="mdiCheckCircle" color="green">
          <template #title>
            {{
              interpolate(pgettext('message', 'Patient ITK %(tic)s successfully saved.'), {
                tic: patientData.tic,
              })
            }}
          </template>
          <template #actions>
            <div class="d-flex flex-column ga-6">
              <v-btn @click="wizard.goNext()" size="large" color="primary">
                {{ pgettext('action', 'Close') }}
              </v-btn>
            </div>
          </template>
        </v-empty-state>
      </WizardStep>
    </Wizard>

    <FormErrorDialog ref="errorDialog"
                     :title="pgettext('title', 'Error while saving identification')"
                     :fields="fieldDefinitions"
                     nonFieldErrorStep="identification"
                     @visitStepsWithErrors="editStep"/>
  </Modal>
</template>
